import { useState, useRef, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Message } from '../types';
import { parseMessageContent, isDuplicateMessage } from '../utils/chatParser';

/**
 * Handles fetching, caching, and real-time syncing of chat messages from Supabase.
 */
export const useChatSync = (userId: string | undefined) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isHistoryLoaded, setIsHistoryLoaded] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const isComponentMountedRef = useRef(true);

    useEffect(() => {
        isComponentMountedRef.current = true;
        return () => { isComponentMountedRef.current = false; };
    }, []);

    // V1002: FALLBACK LOCAL CACHE FOR READ MESSAGES
    const getLocalSeenIds = useCallback(() => {
        try {
            const stored = localStorage.getItem(`aura_seen_messages_${userId}`);
            return stored ? JSON.parse(stored) as string[] : [];
        } catch { return []; }
    }, [userId]);

    const addLocalSeenIds = useCallback((ids: string[]) => {
        if (!userId || !ids.length) return;
        try {
            const current = getLocalSeenIds();
            const updated = Array.from(new Set([...current, ...ids]));
            if (updated.length > 200) updated.splice(0, updated.length - 200);
            localStorage.setItem(`aura_seen_messages_${userId}`, JSON.stringify(updated));
        } catch (e) { console.error("Cache error", e); }
    }, [userId, getLocalSeenIds]);

    const loadHistory = useCallback(async () => {
        if (!userId) return [];

        console.log("[useChatSync] Starting LoadHistory...");
        const { data, error } = await supabase
            .from('chat_history')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: true });

        // V555: SIMULATION MOCK HISTORY
        if (userId === 'mock-user-id-123') {
            console.log("[useChatSync] Simulation Mode: Loading local history.");
            const localHistory = JSON.parse(localStorage.getItem('aura_mock_chat_history') || '[]');
            setIsHistoryLoaded(true);
            setMessages(localHistory);
            return localHistory;
        }

        if (error) {
            console.error("[useChatSync] LoadHistory Error:", error);
            setIsHistoryLoaded(true);
            return [];
        }

        if (!isComponentMountedRef.current) return [];

        const localSeenCache = new Set(getLocalSeenIds());

        const validMessages: Message[] = (data || []).reduce((acc: Message[], m: any) => {
            const parsed = parseMessageContent(m.content, m.created_at);
            if (!parsed) return acc;

            const { content, isFuture, revealTime, actions, isDietReadyMsg } = parsed;

            if (!content && !m.image && !isDietReadyMsg) return acc;
            if (isDuplicateMessage(acc, m, content)) return acc;

            acc.push({
                id: m.id,
                role: m.role as 'user' | 'model',
                content,
                image: m.image || undefined,
                seen: !!m.seen || localSeenCache.has(m.id),
                createdAt: revealTime > 0 ? new Date(revealTime).toISOString() : m.created_at,
                isFuture,
                revealTime,
                actions,
                isDietReadyMsg,
                isRealtime: false // V2106: Explicitly mark history messages so they don't trigger animations
            });
            return acc;
        }, []);

        // Merge logic (HTTP vs WebSockets race condition patch)
        setMessages(prev => {
            const map = new Map<string, Message>();
            validMessages.forEach(m => { if (m.id) map.set(m.id, m); });
            prev.forEach(m => {
                if (m.id) map.set(m.id, m);
                else if (m.createdAt) map.set(m.createdAt, m); // Optimistic local messages
            });
            return Array.from(map.values()).sort((a, b) => {
                const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return timeA - timeB;
            });
        });

        setIsHistoryLoaded(true);
        console.log(`[useChatSync] History loaded: ${validMessages.length} fetched from DB.`);
        return validMessages;
    }, [userId, getLocalSeenIds]);

    // Real-time listener
    useEffect(() => {
        if (!userId || userId === 'mock-user-id-123') return;

        loadHistory();

        const channelName = `chat_history_${userId}_${Math.random().toString(36).substring(7)}`;
        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'chat_history', filter: `user_id=eq.${userId}` },
                (payload) => {
                    console.log("[useChatSync Realtime] 🔴 EVENTO INSERT RECIBIDO:", payload.new);
                    const newData = payload.new;
                    const parsed = parseMessageContent(newData.content, newData.created_at);
                    
                    if (!parsed) {
                        console.log("[useChatSync Realtime] 🔴 Ignorado por parseMessageContent (puede ser un TYPING o PROCESSING).");
                        return;
                    }

                    // --- ACTION TOKEN ROUTING ---
                    // V1200: Diet state mutations are NO LONGER handled via chat realtime tokens.
                    // The backend `process-chat` webhook safely updates the DB `aura_diet_status`
                    // which is then picked up securely by the `DietGlobalWorker` / `useDietStatus` hook.
                    // DO NOT mutate localStorage or emit `aura_diet_updated` here to avoid Race Conditions!
                    
                    parsed.actions.forEach(action => {
                        if (action === 'UPDATE_DIET') {
                            console.log("[useChatSync] Diet/Supplement update queued. Waiting for visual reveal to block UI.");
                            // UI block and DB sync are now securely managed by AIAssistant.tsx 
                            // when the message visually reveals to respect the "Slow Chat" narrative delay.
                        }
                        if (action.startsWith('CHANGE_PROGRAM:') || action.startsWith('SWAP_EXERCISE:')) {
                            // Workouts still need a hard UI refresh event
                            setTimeout(() => {
                                window.dispatchEvent(new Event('aura_workout_updated')); 
                            }, 500);
                        }

                        if (action.startsWith('CHANGE_CHECKIN_DAY:')) {
                            // Fetch the latest profile to get the new check-in day and update local storage
                            setTimeout(async () => {
                                const { data: profile } = await supabase.from('profiles').select('stats').eq('id', userId).single();
                                if (profile && profile.stats) {
                                    localStorage.setItem('aura_user_data', JSON.stringify(profile.stats));
                                    window.dispatchEvent(new Event('storage')); // Force Calendar to repaint
                                }
                            }, 1000); // 1s delay to let the Edge Function finish saving
                        }
                    });

                    const newMsg: Message = {
                        id: newData.id,
                        role: newData.role as 'user' | 'model',
                        content: parsed.content,
                        image: newData.image || undefined,
                        createdAt: parsed.revealTime > 0 ? new Date(parsed.revealTime).toISOString() : newData.created_at,
                        seen: newData.seen,
                        isFuture: parsed.isFuture,
                        revealTime: parsed.revealTime,
                        actions: parsed.actions,
                        isDietReadyMsg: parsed.isDietReadyMsg,
                        isRealtime: true // V2106: Mark as live message!
                    };

                    console.log("[useChatSync Realtime] 🔴 Intentando añadir a React state:", newMsg);

                    setMessages(prev => {
                        if (prev.some(m => m.id === newMsg.id)) return prev;
                        if (isDuplicateMessage(prev, newData, parsed.content)) return prev;
                        return [...prev, newMsg];
                    });
                }
            )
            .on(
                'postgres_changes',
                // FIX V2026: DO NOT USE `user_id` filter for DELETE events!
                // Supabase REPLICA IDENTITY DEFAULT means `payload.old` only has `{id}`.
                // The filter `user_id=eq...` evaluates to false and the websocket drops the packet.
                { event: 'DELETE', schema: 'public', table: 'chat_history' },
                (payload) => {
                    console.log("[useChatSync Realtime] 🛑 EVENTO DELETE RECIBIDO:", payload.old);
                    setMessages(prev => prev.filter(m => m.id !== payload.old.id));
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'chat_history', filter: `user_id=eq.${userId}` },
                (payload) => {
                    const updated = payload.new;
                    const parsed = parseMessageContent(updated.content, updated.created_at);
                    if (parsed) {
                        setMessages(prev => prev.map(m => {
                            if (m.id !== updated.id) return m;
                            return {
                                ...m,
                                content: parsed.content,
                                createdAt: parsed.revealTime > 0 ? new Date(parsed.revealTime).toISOString() : updated.created_at,
                                isFuture: parsed.isFuture,
                                revealTime: parsed.revealTime,
                                seen: m.seen || !!updated.seen,
                                actions: parsed.actions,
                                isDietReadyMsg: parsed.isDietReadyMsg
                            };
                        }));
                    }
                }
            )
            .subscribe((status, err) => {
                console.log(`[useChatSync Realtime] Status: ${status}`, err || '');
                // V2203: Auto-reconnect if the socket drops
                if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
                    console.warn(`[useChatSync Realtime] Connection dropped. Reconnecting in 5s (Attempt ${retryCount + 1})...`);
                    setTimeout(() => {
                        if (isComponentMountedRef.current) {
                            setRetryCount(prev => prev + 1);
                        }
                    }, 5000);
                }
            });

        return () => {
            console.log(`[useChatSync Realtime] Removing channel`);
            supabase.removeChannel(channel);
        };
    }, [userId, loadHistory, retryCount]);

    return {
        messages,
        setMessages,
        isHistoryLoaded,
        loadHistory,
        addLocalSeenIds
    };
};
